import { ComputerAgentResults, Resource } from "@/components/VibeChat/stores/types";
import { EntityType } from "@/queries/folder-entities/types";
import { routePaths } from "@/utils/routePaths";
import { RESOURCE_TYPES, ResourceType } from "@/components/VibeChat/types";

const VALID_RESOURCE_TYPES = new Set<string>(Object.values(RESOURCE_TYPES));

export interface DetectedResource {
  type: ResourceType;
  id: number;
  name: string;
  url: string;
}

export const RESOURCE_LABELS: Record<ResourceType, string> = {
  prompt: "Prompt",
  dataset: "Dataset",
  blueprint: "Evaluation",
  folder: "Folder",
  workflow: "Workflow",
};

export const RESOURCE_ENTITY_TYPE: Record<ResourceType, EntityType> = {
  prompt: EntityType.PROMPT,
  dataset: EntityType.DATASET,
  blueprint: EntityType.REPORT,
  folder: EntityType.FOLDER,
  workflow: EntityType.WORKFLOW,
};

const RESOURCE_URL_BUILDERS: Record<ResourceType, (wid: number, id: number) => string> = {
  prompt: routePaths.workspacePrompt,
  dataset: routePaths.workspaceDataset,
  blueprint: routePaths.workspaceBlueprint,
  folder: routePaths.workspaceFolder,
  workflow: routePaths.workspaceWorkflow,
};

const isValidResource = (r: Resource): boolean =>
  VALID_RESOURCE_TYPES.has(r.type) && typeof r.id === "number" && typeof r.name === "string";

export const detectResources = (
  results: ComputerAgentResults,
  workspaceId: number,
): DetectedResource[] => {
  if (!Array.isArray(results.resources)) {
    return [];
  }
  return results.resources.filter(isValidResource).map(r => ({
    type: r.type,
    id: r.id,
    name: r.name,
    url: RESOURCE_URL_BUILDERS[r.type](workspaceId, r.id),
  }));
};

export const buildDedupKey = (resource: DetectedResource): string =>
  `${resource.type}-${resource.id}`;

const URL_ENTITY_PATTERNS: [RegExp, EntityType][] = [
  [/\/smart-tables\//, EntityType.SMART_TABLE],
  [/\/dataset-groups\//, EntityType.DATASET],
  [/\/evaluate\/(blueprints|reports)\//, EntityType.REPORT],
  [/\/ab-releases\//, EntityType.AB_TEST],
  [/\/input-variable-set\//, EntityType.INPUT_VARIABLE_SET],
  [/\/workflow\//, EntityType.WORKFLOW],
  [/\/folder\//, EntityType.FOLDER],
  [/\/prompt\//, EntityType.PROMPT],
];

export const detectEntityTypeFromUrl = (url: string): EntityType | null => {
  for (const [pattern, entityType] of URL_ENTITY_PATTERNS) {
    if (pattern.test(url)) {
      return entityType;
    }
  }
  return null;
};
